/**
 * Утилита для настройки темы сайта из переменных окружения
 */

/**
 * Преобразует hex цвет в RGB значения
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Осветляет цвет на указанный процент
 */
function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * (percent / 100)));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * (percent / 100)));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * (percent / 100)));

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Затемняет цвет на указанный процент
 */
function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.max(0, Math.round(rgb.r * (1 - percent / 100)));
  const g = Math.max(0, Math.round(rgb.g * (1 - percent / 100)));
  const b = Math.max(0, Math.round(rgb.b * (1 - percent / 100)));

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Генерирует палитру цветов на основе основного цвета
 */
function generateColorPalette(baseColor: string): {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
} {
  return {
    50: lightenColor(baseColor, 90),
    100: lightenColor(baseColor, 80),
    200: lightenColor(baseColor, 60),
    300: lightenColor(baseColor, 40),
    400: lightenColor(baseColor, 20),
    500: baseColor,
    600: darkenColor(baseColor, 10),
    700: darkenColor(baseColor, 20),
    800: darkenColor(baseColor, 30),
    900: darkenColor(baseColor, 40),
  };
}

/**
 * Преобразует hex цвет в RGB значения для rgba
 */
function hexToRgbString(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
}

/**
 * Устанавливает CSS переменные для темы на основе переменных окружения
 */
export function setupTheme(): void {
  const root = document.documentElement;
  
  // Получаем основной цвет из переменных окружения или используем фиолетовый по умолчанию
  const primaryColor = process.env.REACT_APP_PRIMARY_COLOR || '#a855f7';
  const primaryColorLight = process.env.REACT_APP_PRIMARY_COLOR_LIGHT;
  const primaryColorDark = process.env.REACT_APP_PRIMARY_COLOR_DARK;

  // Генерируем палитру цветов
  const palette = generateColorPalette(primaryColor);

  // Устанавливаем CSS переменные
  root.style.setProperty('--primary-color', primaryColor);
  root.style.setProperty('--primary-color-light', primaryColorLight || palette[400]);
  root.style.setProperty('--primary-color-dark', primaryColorDark || palette[700]);
  
  // Устанавливаем всю палитру
  Object.entries(palette).forEach(([key, value]) => {
    root.style.setProperty(`--primary-color-${key}`, value);
    
    // Генерируем цвета с opacity для border
    const rgbString = hexToRgbString(value);
    if (rgbString) {
      if (key === '800') {
        root.style.setProperty(`--primary-color-${key}-20`, `rgba(${rgbString}, 0.2)`);
        root.style.setProperty(`--primary-color-${key}-30`, `rgba(${rgbString}, 0.3)`);
        root.style.setProperty(`--primary-color-${key}-50`, `rgba(${rgbString}, 0.5)`);
      }
      if (key === '600') {
        root.style.setProperty(`--primary-color-${key}-20`, `rgba(${rgbString}, 0.2)`);
        root.style.setProperty(`--primary-color-${key}-50`, `rgba(${rgbString}, 0.5)`);
      }
      if (key === '500') {
        root.style.setProperty(`--primary-color-${key}-25`, `rgba(${rgbString}, 0.25)`);
        root.style.setProperty(`--primary-color-${key}-50`, `rgba(${rgbString}, 0.5)`);
      }
    }
  });
}

/**
 * Получает название сайта из переменных окружения
 */
export function getSiteName(): string {
  return process.env.REACT_APP_SITE_NAME || 'GEKATA';
}

/**
 * Получает первую букву названия сайта для логотипа
 */
export function getSiteInitial(): string {
  const siteName = getSiteName();
  return siteName.charAt(0).toUpperCase();
}

